import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Space, apiService } from '../services/api';
import { dashboardCache } from '../services/dashboardCache';
import { useSpacesQuery, useCreateSpaceMutation, useUpdateSpaceMutation, useDeleteSpaceMutation } from '../hooks/useSpacesQuery';

interface SpaceContextType {
  // Current space state
  currentSpace: Space | null;
  availableSpaces: Space[];
  totalSpaces: number;
  spaceLimit: number;
  
  // Loading states
  isLoadingSpaces: boolean;
  /** Set when the spaces list request failed. Empty UI must not treat this as zero spaces. */
  spacesError: Error | null;
  
  // Actions
  setCurrentSpace: (space: Space | null) => void;
  refreshSpaces: () => Promise<void>;
  createSpace: (spaceData: { name: string; icon?: string; color?: string; template_type?: string }) => Promise<Space>;
  updateCurrentSpace: (updates: { name?: string; icon?: string; color?: string }) => Promise<void>;
  deleteSpace: (spaceId: string) => Promise<void>;
  
  // 3D Spatial effects
  isTransitioningSpaces: boolean;
  enterSpace: (space: Space) => Promise<void>;
  exitSpace: () => void;
  
  // Helper functions
  getSpaceByIdOrFirst: (spaceId?: string) => Space | null;
}

const SpaceContext = createContext<SpaceContextType | undefined>(undefined);

interface SpaceProviderProps {
  children: ReactNode;
}

export function SpaceProvider({ children }: SpaceProviderProps) {
  // Use query hook for data fetching
  const spacesQuery = useSpacesQuery();
  const createMutation = useCreateSpaceMutation();
  const updateMutation = useUpdateSpaceMutation();
  const deleteMutation = useDeleteSpaceMutation();

  // UI state only (no refs for data)
  const [currentSpace, setCurrentSpaceState] = useState<Space | null>(null);
  const [isTransitioningSpaces, setIsTransitioningSpaces] = useState(false);

  // Auto-select first space when spaces load and no current space is selected
  useEffect(() => {
    if (spacesQuery.spaces.length > 0 && !currentSpace && !spacesQuery.isLoading) {
      console.log('🔄 Auto-selecting first space:', spacesQuery.spaces[0].name);
      setCurrentSpaceState(spacesQuery.spaces[0]);
    }
  }, [spacesQuery.spaces, spacesQuery.isLoading, currentSpace]);

  const setCurrentSpace = useCallback((space: Space | null) => {
    setCurrentSpaceState(space);
    if (space) {
      // Touch the space to update last accessed (fire and forget)
      apiService.touchSpace(space.space_id).catch(err => {
        console.warn('Failed to update space access time:', err);
      });
    }
  }, []);

  const enterSpace = useCallback(async (space: Space): Promise<void> => {
    setIsTransitioningSpaces(true);
    try {
      // Add spatial transition effect timing
      await new Promise(resolve => setTimeout(resolve, 300));
      setCurrentSpace(space);
    } finally {
      setIsTransitioningSpaces(false);
    }
  }, [setCurrentSpace]);

  const exitSpace = useCallback(() => {
    setCurrentSpaceState(null);
  }, []);

  const refreshSpaces = useCallback(async () => {
    await spacesQuery.refetch();
  }, [spacesQuery]);

  const createSpace = useCallback(async (spaceData: { name: string; icon?: string; color?: string; template_type?: string }): Promise<Space> => {
    const newSpace = await createMutation.mutate(spaceData);
    
    // Invalidate dashboard cache when spaces change
    dashboardCache.onSpaceChange();
    
    // Set as current space
    setCurrentSpace(newSpace);
    
    return newSpace;
  }, [createMutation, setCurrentSpace]);

  const updateCurrentSpace = useCallback(async (updates: { name?: string; icon?: string; color?: string }): Promise<void> => {
    if (!currentSpace) {
      throw new Error('No current space to update');
    }
    
    const updatedSpace = await updateMutation.mutate(currentSpace.space_id, updates);
    
    // Update current space if it's the one being updated
    if (currentSpace.space_id === updatedSpace.space_id) {
      setCurrentSpaceState(updatedSpace);
    }
  }, [currentSpace, updateMutation]);

  const deleteSpace = useCallback(async (spaceId: string): Promise<void> => {
    await deleteMutation.mutate(spaceId);
    
    // Invalidate dashboard cache when spaces change
    dashboardCache.onSpaceChange();
    
    // If deleting current space, clear it
    if (currentSpace?.space_id === spaceId) {
      setCurrentSpaceState(null);
    }
  }, [deleteMutation, currentSpace]);

  const getSpaceByIdOrFirst = useCallback((spaceId?: string): Space | null => {
    if (!spaceId) return spacesQuery.spaces[0] || null;
    return spacesQuery.spaces.find(space => space.space_id === spaceId) || spacesQuery.spaces[0] || null;
  }, [spacesQuery.spaces]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue: SpaceContextType = useMemo(() => ({
    currentSpace,
    availableSpaces: spacesQuery.spaces,
    totalSpaces: spacesQuery.totalCount,
    spaceLimit: spacesQuery.spaceLimit,
    isLoadingSpaces: spacesQuery.isLoading,
    spacesError: spacesQuery.error,
    setCurrentSpace,
    refreshSpaces,
    createSpace,
    updateCurrentSpace,
    deleteSpace,
    isTransitioningSpaces,
    enterSpace,
    exitSpace,
    getSpaceByIdOrFirst,
  }), [
    currentSpace,
    spacesQuery.spaces,
    spacesQuery.totalCount,
    spacesQuery.spaceLimit,
    spacesQuery.isLoading,
    spacesQuery.error,
    setCurrentSpace,
    refreshSpaces,
    createSpace,
    updateCurrentSpace,
    deleteSpace,
    isTransitioningSpaces,
    enterSpace,
    exitSpace,
    getSpaceByIdOrFirst,
  ]);

  return (
    <SpaceContext.Provider value={contextValue}>
      {children}
    </SpaceContext.Provider>
  );
}

export function useSpace(): SpaceContextType {
  const context = useContext(SpaceContext);
  if (context === undefined) {
    throw new Error('useSpace must be used within a SpaceProvider');
  }
  return context;
}

/**
 * Non-throwing variant of {@link useSpace}.
 *
 * Returns `undefined` when the consumer is rendered outside a `SpaceProvider`
 * (e.g. public marketing pages such as `/blog` or `/roadmap/*`). Use this in
 * shared hooks/components that are mounted in both authenticated and
 * unauthenticated trees and that can degrade gracefully without a current
 * space.
 */
export function useSpaceOptional(): SpaceContextType | undefined {
  return useContext(SpaceContext);
}

export default SpaceContext; 